import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { GACHA_TYPE, GRADE, SSR_TYPE, tryGacha } from "../../../functions/gachaFunctions";
import { getSaviorByNameTitle } from "../../../functions/getSaviorFunctions";
import { getArcanaByName } from "../../../functions/getArcanaFunctions";
import { loadData } from "../../../functions/dataLoader";
import GACHA_DATA from "../../../config/gacha.json";

const COSMIC_ATTR = ["질서", "혼돈"];

const makeInitialState = (pickupHasCosmic = false) => ({
    savior: { total: 0, ssr: 0, pickup: 0, results: {}, latestList: [] },
    arcana: { total: 0, ssr: 0, pickup: 0, results: {}, latestList: [] },
    snipe: { total: 0, ssr: 0, pickup: 0, results: {}, latestList: [], type: { value: "savior", name: "구원자" }, target: "" },
    isPickup: false,
    pickupHasCosmic,
    saviorPickupType: SSR_TYPE.NOT_COSMIC,
    status: "idle",
});

export const initGacha = createAsyncThunk("gacha/init", async () => {
    const [originSaviorData] = await Promise.all([loadData("saviors.json")]);
    const pickupHasCosmic =
        GACHA_DATA.SAVIOR_PICKUPS.find((id) => {
            const savior = originSaviorData.find((s) => s.id === id);
            return savior && COSMIC_ATTR.includes(savior.attr);
        }) !== undefined;
    return { pickupHasCosmic };
});

const gachaSlice = createSlice({
    name: "gacha",
    initialState: makeInitialState(),
    reducers: {
        tryTen: (state, action) => {
            const lang = action.payload.lang;
            state[action.payload.type.value].latestList = [];

            let type = action.payload.type.value;
            let pickupType = state.pickupHasCosmic ? state.saviorPickupType : SSR_TYPE.NOT_COSMIC;
            let pickupIndex;

            if (type === GACHA_TYPE.SNIPE) {
                type = state.snipe.type.value;
                if (type === "savior") {
                    const target = getSaviorByNameTitle(lang, state.snipe.target);
                    pickupType = COSMIC_ATTR.includes(target.attr) ? SSR_TYPE.COSMIC : SSR_TYPE.NOT_COSMIC;
                    // loadData 캐시에서 꺼내야 하므로 gachaFunctions에서 GACHA_DATA 참조하는 방식 유지
                    pickupIndex = GACHA_DATA.SAVIOR_PICKUPS.findIndex((e) => e === target.id); // tryGacha 내부에서 처리
                } else if (type === "arcana") {
                    const target = getArcanaByName(lang, state.snipe.target);
                    pickupIndex = GACHA_DATA.ARCANA_PICKUPS.findIndex((e) => e === target.id);
                }
            } else if (state.isPickup) {
                pickupIndex = 0;
            }

            for (let i = 0; i < 10; i++) {
                const result =
                    type === GACHA_TYPE.SAVIOR
                        ? tryGacha(lang, type, state.isPickup, pickupType, pickupIndex)
                        : tryGacha(lang, type, state.isPickup, SSR_TYPE.NOT_COSMIC, pickupIndex);

                if (result.id !== GRADE.R) {
                    if (result.isSSR) state[action.payload.type.value].ssr++;
                    if (result.isPickup) state[action.payload.type.value].pickup++;

                    if (!state[action.payload.type.value].results[result.name]) {
                        state[action.payload.type.value].results[result.name] = result;
                        state[action.payload.type.value].results[result.name].count = 0;
                    }
                    state[action.payload.type.value].results[result.name].count++;
                }

                const resultWithType = {
                    ...result,
                    gachaType:
                        action.payload.type.value === GACHA_TYPE.SNIPE
                            ? state.snipe.type.value
                            : action.payload.type.value === GACHA_TYPE.ARCANA
                              ? "arcana"
                              : "savior",
                };

                state[action.payload.type.value].latestList.push(resultWithType);
                state[action.payload.type.value].total++;
            }
        },
        tryOne: (state, action) => {
            const lang = action.payload.lang;
            state[action.payload.type.value].latestList = [];

            if (action.payload.type === GACHA_TYPE.SNIPE) {
                // 스나이프 1회 뽑기 로직은 별도로 구현 필요
            } else {
                const result =
                    action.payload.type.value === GACHA_TYPE.SAVIOR
                        ? tryGacha(lang, action.payload.type.value, state.isPickup, state.saviorPickupType)
                        : tryGacha(lang, action.payload.type.value, state.isPickup, SSR_TYPE.NOT_COSMIC);

                if (result !== GRADE.R) {
                    if (result.isSSR) state[action.payload.type.value].ssr++;
                    if (result.isPickup) state[action.payload.type.value].pickup++;

                    if (!state[action.payload.type.value].results[result.name]) {
                        state[action.payload.type.value].results[result.name] = result;
                        state[action.payload.type.value].results[result.name].count = 0;
                    }
                    state[action.payload.type.value].results[result.name].count++;
                }

                const resultWithType = {
                    ...result,
                    gachaType: action.payload.type.value === GACHA_TYPE.ARCANA ? "아르카나" : "구원자",
                };

                state[action.payload.type.value].latestList.push(resultWithType);
                state[action.payload.type.value].total++;
            }
        },
        setSnipeType: (state, action) => {
            state.snipe.type = action.payload.obj;
        },
        setSnipeTarget: (state, action) => {
            state.snipe.target = action.payload;
        },
        setIsPickup: (state, action) => {
            state.isPickup = action.payload;
        },
        setSaviorPickupType: (state, action) => {
            state.saviorPickupType = action.payload;
            console.log(action.payload);
        },
        reset: (state, action) => {
            if (action.payload.value === GACHA_TYPE.SNIPE) {
                state.snipe = {
                    total: 0,
                    ssr: 0,
                    pickup: 0,
                    results: {},
                    latestList: [],
                    type: state.snipe.type,
                    target: state.snipe.target,
                };
            } else {
                state[action.payload.value] = makeInitialState()[action.payload.value];
            }
        },
        resetAll: (state) => makeInitialState(state.pickupHasCosmic),
    },
    extraReducers: (builder) => {
        builder.addCase(initGacha.fulfilled, (state, action) => {
            state.pickupHasCosmic = action.payload.pickupHasCosmic;
            state.status = "done";
        });
    },
});

export const { tryOne, tryTen, setSnipeType, setSnipeTarget, setIsPickup, setSaviorPickupType, reset, resetAll } = gachaSlice.actions;
export default gachaSlice.reducer;
